import { useRef } from "react";
import { PiArrowUpLeft, PiPrinter, PiShoppingBag } from "react-icons/pi";
import { publicImageProps } from "../../../../services/config";
import { convertDate } from "../../../../utils/convertDate";
import { digitsThree } from "../../../../utils/price";
import ExportModal from "../../../../components/common/modals/export-modal/ExportModal";

const Field = ({ label, children, className = "" }) => (
  <div className={`min-w-0 ${className}`}>
    <dt className="mb-1 text-xs text-slate-500 dark:text-slate-400">{label}</dt>
    <dd className="break-words text-sm font-medium text-slate-800 dark:text-slate-100">
      {children || "—"}
    </dd>
  </div>
);

function OrderCard({ data, getOrderInfo, utils, reGetData, showImages = true }) {
  const exportRef = useRef();
  const paid = data?.paymentStatus === "done";
  const shippingStatus = utils?.ORDER_PROCCESS_SHIPPING_STATUS?.[data?.proccessShipping]?.name;
  const paymentStatus = utils?.PAYMENT_STATUS?.[data?.paymentStatus]?.name;
  const address = [data?.province, data?.city, data?.address].filter(Boolean).join("، ");
  const images = data?.products?.filter((item) => item?.product?.mainImage) || [];

  return (
    <>
      <ExportModal ref={exportRef} productData={data} reGetData={reGetData} />
      <article className="w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md dark:border-slate-700 dark:bg-slate-800">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 px-4 py-4 sm:px-6 dark:border-slate-700">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="text-base font-bold text-slate-900 dark:text-white">
                سفارش <span dir="ltr" className="inline-block">#{data?.trackingCode || "—"}</span>
              </h4>
              <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${paid ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" : "bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"}`}>
                {paymentStatus || (paid ? "پرداخت شده" : "پرداخت نشده")}
              </span>
              {paid && shippingStatus && (
                <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                  {shippingStatus}
                </span>
              )}
            </div>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              {data?.paymentDate ? `پرداخت: ${convertDate(data.paymentDate, true)}` : data?.createdAt ? `ثبت: ${convertDate(data.createdAt, true)}` : "تاریخ ثبت نامشخص"}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => getOrderInfo(data?._id)} className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-700">
              <PiArrowUpLeft size={17} aria-hidden="true" /> جزئیات و ویرایش
            </button>
            {paid && (
              <button type="button" onClick={() => exportRef.current?.showModal()} className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-emerald-700 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-500">
                <PiPrinter size={17} aria-hidden="true" /> تایید و پرینت
              </button>
            )}
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-x-5 gap-y-5 px-4 py-5 sm:grid-cols-3 sm:px-6 lg:grid-cols-4">
          <Field label="نام گیرنده">{data?.name}</Field>
          <Field label="مبلغ نهایی">{digitsThree(data?.finalPrice ?? 0)} تومان</Field>
          <Field label="تخفیف">{digitsThree(data?.totalDiscount ?? 0)} تومان</Field>
          <Field label="بارکد"><span dir="ltr" className="inline-block">{data?.barcode || "—"}</span></Field>
          <Field label="آدرس" className="col-span-2 sm:col-span-3 lg:col-span-4">{address}</Field>
          {data?.exportingDate && <Field label="تاریخ بسته‌بندی">{convertDate(data.exportingDate, true)}</Field>}
        </dl>

        {showImages && data?.products?.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 px-4 py-3 sm:px-6 dark:border-slate-700">
            <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 dark:text-slate-400">
              <PiShoppingBag size={18} aria-hidden="true" /> {data.products.length} قلم
            </span>
            <div className="flex flex-wrap gap-2">
              {images.slice(0, 6).map((item, index) => (
                <img key={`${item?.product?._id || index}-${index}`} {...publicImageProps(item.product.mainImage, { variant: "thumb_" })} alt={item.productName || item.product?.name || "تصویر محصول"} loading="lazy" className="h-11 w-11 rounded-lg border border-slate-200 bg-slate-50 object-cover dark:border-slate-600" />
              ))}
              {images.length > 6 && <span className="flex h-11 min-w-11 items-center justify-center rounded-lg bg-slate-100 px-2 text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-200">+{images.length - 6}</span>}
            </div>
          </div>
        )}
      </article>
    </>
  );
}

export default OrderCard;
